/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from "react";

import { XboxSpinner } from "@gamedev-web/gamedev-sdk-core";
import { Close, Search } from "@gaming/xds-react-js-icons";

import { getImage, ImageType } from "../../helpers/imageHelper";
import { useStrings } from "../../localization/localizationHelpers";
import * as styles from "./ConceptsManager.module.css";

export type ConceptItem = {
  id: string;
  title: string;
  statusLabel: string;
  statusDotClass: string;
  statusLabelClass: string;
  datePrefix: string;
  formattedDate: string | null;
  onClick: () => void;
};

export type FilterOption = {
  id: string;
  label: string;
};

export type ConceptsManagerConfig = {
  title: string;
  createButtonLabel: string;
  searchPlaceholder: string;
  emptyStateTitle: string;
  emptyStateDescription: string;
  emptyStateButtonLabel: string;
  noResultsMessage?: string;
  filterOptions?: FilterOption[];
  sortOptions?: FilterOption[];
};

export type ConceptsManagerProps = Readonly<{
  config: ConceptsManagerConfig;
  items: ConceptItem[] | undefined;
  isLoading: boolean;
  hasNeverCreated: boolean;
  onCreateNew?: () => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onSearchChange?: (searchBy: string) => void;
  onFilterChange?: (filter: string) => void;
  onSortChange?: (orderBy: string) => void;
  /** Optional custom row renderer. When provided, replaces the default row rendering. */
  renderRow?: (item: ConceptItem, index: number) => React.ReactNode;
  /** Optional extra content rendered in the header actions (e.g., filter button). */
  renderHeaderActions?: () => React.ReactNode;
  /** Optional content rendered between header and list (e.g., filter popup). */
  renderBelowHeader?: () => React.ReactNode;
}>;

export const ConceptsManager = ({
  config,
  items,
  isLoading,
  hasNeverCreated,
  onCreateNew,
  onLoadMore,
  hasMore,
  isLoadingMore,
  onSearchChange,
  renderRow,
  renderHeaderActions,
  renderBelowHeader,
}: ConceptsManagerProps) => {
  const [
    closeSearchLabel,
    searchLabel,
    noResultsFound,
    emptyStateImageAlt,
    hubAccessRequiredTitle,
    hubAccessRequiredDescription,
  ] = useStrings(
    "closeSearchAriaLabel",
    "searchAriaLabel",
    "noResultsFound",
    "gameConceptsManagerEmptyStateImageAlt",
    "hubAccessRequiredTitle",
    "hubAccessRequiredDescription"
  );
  const [showSearch, setShowSearch] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  const gotItems = items && items.length > 0;

  // Infinite scroll: observe a sentinel element at the bottom of the list
  const sentinelRef = useRef<HTMLDivElement>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  const hasMoreRef = useRef(hasMore);
  const isLoadingMoreRef = useRef(isLoadingMore);

  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
    hasMoreRef.current = hasMore;
    isLoadingMoreRef.current = isLoadingMore;
  }, [onLoadMore, hasMore, isLoadingMore]);

  useEffect(() => {
    if (showSearch) {
      searchInputRef.current?.focus();
    }
  }, [showSearch]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMoreRef.current && !isLoadingMoreRef.current) {
          onLoadMoreRef.current?.();
        }
      },
      { threshold: 1.0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [items]);

  if (isLoading && !items) {
    return (
      <div className={styles.emptyState}>
        <XboxSpinner />
      </div>
    );
  }

  if (hasNeverCreated && onCreateNew) {
    return (
      <div className={styles.emptyState}>
        <img
          className={styles.emptyStateImage}
          src={getImage("controller", ImageType.Png)}
          alt={emptyStateImageAlt}
        />
        <h2 className={styles.emptyStateTitle}>{config.emptyStateTitle}</h2>
        <p className={styles.emptyStateDescription}>{config.emptyStateDescription}</p>
        <button type="button" className={styles.emptyStateButton} onClick={onCreateNew}>
          {config.emptyStateButtonLabel}
        </button>
      </div>
    );
  }

  if (hasNeverCreated) {
    return (
      <div className={styles.emptyState}>
        <h2 className={styles.emptyStateTitle}>{hubAccessRequiredTitle}</h2>
        <p className={styles.emptyStateDescription}>{hubAccessRequiredDescription}</p>
      </div>
    );
  }

  return (
    <div className={styles.managerContainer}>
      <div className={styles.managerHeader}>
        {!showSearch && <h2 className={styles.managerTitle}>{config.title}</h2>}
        {showSearch && (
          <div className={styles.searchExpanded}>
            <span className={styles.searchIcon}>
              <Search />
            </span>
            <input
              ref={searchInputRef}
              type="text"
              className={styles.searchInput}
              placeholder={config.searchPlaceholder}
              value={searchValue}
              onChange={e => {
                setSearchValue(e.target.value);
                onSearchChange?.(e.target.value);
              }}
            />
            <button
              type="button"
              className={styles.searchClose}
              onClick={() => {
                setShowSearch(false);
                setSearchValue("");
                onSearchChange?.("");
              }}
              aria-label={closeSearchLabel}
            >
              <Close />
            </button>
          </div>
        )}
        <div className={styles.headerActions}>
          {!showSearch && onSearchChange && (
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => setShowSearch(true)}
              aria-label={searchLabel}
            >
              <Search />
            </button>
          )}
          {renderHeaderActions?.()}
          {onCreateNew && (
            <button type="button" className={styles.createButton} onClick={onCreateNew}>
              {config.createButtonLabel}
            </button>
          )}
        </div>
      </div>

      {renderBelowHeader?.()}

      {isLoading && (
        <div className={styles.spinnerFullWidth}>
          <XboxSpinner />
        </div>
      )}
      {!isLoading && (
        <div className={styles.conceptList}>
          {!gotItems && (
            <p className={styles.noConceptsMessage}>{config.noResultsMessage ?? noResultsFound}</p>
          )}
          {items?.map((item, index) =>
            renderRow ? (
              <React.Fragment key={item.id}>{renderRow(item, index)}</React.Fragment>
            ) : (
              <div
                key={item.id}
                className={styles.conceptRow}
                onClick={item.onClick}
                role="button"
                tabIndex={0}
                onKeyDown={e => (e.key === "Enter" || e.key === " ") && item.onClick()}
              >
                <div className={styles.conceptInfo}>
                  <h3 className={styles.conceptTitle}>{item.title}</h3>
                  <div className={styles.conceptMeta}>
                    <span className={`${styles.statusDot} ${item.statusDotClass}`} />
                    <span className={`${styles.statusLabel} ${item.statusLabelClass}`}>
                      {item.statusLabel}
                    </span>
                    {item.formattedDate && (
                      <span className={styles.lastEdited}>
                        {item.datePrefix} {item.formattedDate}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      )}
      {gotItems && (hasMore || isLoadingMore) && (
        <div ref={sentinelRef} className={styles.infiniteScrollSentinel}>
          {isLoadingMore && <XboxSpinner size={48} />}
        </div>
      )}
    </div>
  );
};
